// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useId,
  useLayoutEffect,
} from 'react';
import React from 'react';
import { createPortal } from 'react-dom';
import { DropdownChevronIcon } from './TablePaginationIcon';
import './Dropdown.css';

export interface DropdownOption {
  value: string;
  text: string;
}

export interface DropdownProps {
  label?: string;
  placeholder?: string;
  options: DropdownOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  fluid?: boolean;
  search?: boolean;
  clearable?: boolean;
  /**
   * `default` — a full form control (bordered box, label, 48px height).
   * `inline` — compact plain-text trigger for dense UIs (table pagination).
   * `field` — borderless in-field unit selector that sits INSIDE another
   *   bordered box (e.g. the money amount + "Annually" or term "Months/Years"),
   *   reading as part of that field rather than a separate control.
   */
  variant?: 'default' | 'inline' | 'field';
  className?: string;
  id?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
  'data-testid'?: string;
}

export function Dropdown({
  label,
  placeholder = 'Select...',
  options,
  value,
  onChange,
  error,
  helperText,
  required,
  disabled,
  fluid = false,
  search = false,
  clearable = false,
  variant = 'default',
  className = '',
  id: idProp,
  style,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: DropdownProps) {
  const autoId = useId();
  const id = idProp ?? autoId;

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  // Type-ahead (native-<select>-style): accumulate typed letters briefly and
  // jump the highlight to the first matching option. Only used when the search
  // input is off (with search on, typing filters instead).
  const typeahead = useRef<{ buffer: string; timer: ReturnType<typeof setTimeout> | undefined }>({
    buffer: '',
    timer: undefined,
  });
  const highlightedIndexRef = useRef(-1);

  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const isFilled = value !== undefined && value !== '';

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions =
    search && searchQuery
      ? options.filter((opt) =>
          opt.text.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : options;

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Focus search input when opened in search mode
  useEffect(() => {
    if (isOpen && search && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, search]);

  // Position the portal menu relative to the trigger.
  // Flips above the trigger when there isn't enough space below.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      // Use the actual rendered menu height when available so single-option
      // menus don't reserve unnecessary space; fall back to the CSS cap.
      const menuHeight = menuRef.current?.offsetHeight ?? 240;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const flipUp =
        spaceBelow < menuHeight + gap && spaceAbove > spaceBelow;
      // The inline/field variants let the menu pick its own (CSS-driven) width
      // so a narrow trigger ("5" / "Annually") doesn't squash the option list.
      const menuOwnsWidth = variant !== 'default';

      setMenuStyle({
        position: 'fixed',
        top: flipUp ? undefined : rect.bottom + gap,
        bottom: flipUp ? window.innerHeight - rect.top + gap : undefined,
        left: rect.left,
        width: menuOwnsWidth ? undefined : rect.width,
        zIndex: 9999,
      });
    };

    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Keep a synchronous mirror of the highlight for type-ahead cycling.
  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex;
  }, [highlightedIndex]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && menuRef.current) {
      const items = menuRef.current.querySelectorAll('[role="option"]');
      const item = items[highlightedIndex];
      if (item && typeof item.scrollIntoView === 'function') {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(-1);
    setSearchQuery('');
  }, [disabled]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  }, []);

  // Jump the highlight to the first option whose text starts with the typed
  // buffer. Repeating the same letter cycles through matches (native behaviour).
  const typeAhead = useCallback(
    (char: string) => {
      const list = filteredOptions;
      if (list.length === 0) return;
      clearTimeout(typeahead.current.timer);
      const buffer = (typeahead.current.buffer + char).toLowerCase();
      typeahead.current.buffer = buffer;
      typeahead.current.timer = setTimeout(() => {
        typeahead.current.buffer = '';
      }, 500);
      const sameChar = buffer.length > 1 && [...buffer].every((c) => c === buffer[0]);
      const needle = sameChar ? buffer[0] : buffer;
      const from = sameChar ? highlightedIndexRef.current + 1 : 0;
      for (let i = 0; i < list.length; i += 1) {
        const idx = (from + i) % list.length;
        if (list[idx].text.toLowerCase().startsWith(needle)) {
          setHighlightedIndex(idx);
          return;
        }
      }
    },
    [filteredOptions],
  );

  const selectOption = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue);
      closeMenu();
      triggerRef.current?.focus();
    },
    [onChange, closeMenu],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.('');
      triggerRef.current?.focus();
    },
    [onChange],
  );

  const handleTriggerClick = useCallback(() => {
    if (disabled) return;
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [disabled, isOpen, closeMenu, openMenu]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        case 'Escape':
          if (isOpen) {
            e.preventDefault();
            closeMenu();
            triggerRef.current?.focus();
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            openMenu();
          } else {
            setHighlightedIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0,
            );
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) {
            openMenu();
          } else {
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1,
            );
          }
          break;
        case 'Enter':
        case ' ':
          if (isOpen && highlightedIndex >= 0) {
            e.preventDefault();
            selectOption(filteredOptions[highlightedIndex].value);
          } else if (!isOpen && e.key === 'Enter') {
            e.preventDefault();
            openMenu();
          } else if (!isOpen && e.key === ' ') {
            e.preventDefault();
            openMenu();
          }
          break;
        case 'Tab':
          if (isOpen) {
            closeMenu();
          }
          break;
        default:
          // Type-ahead: a printable key jumps to the first matching option.
          // Skipped when the search input is on (it owns typing) or with a
          // modifier held.
          if (
            !search &&
            e.key.length === 1 &&
            !e.metaKey &&
            !e.ctrlKey &&
            !e.altKey
          ) {
            e.preventDefault();
            if (!isOpen) openMenu();
            typeAhead(e.key);
          }
      }
    },
    [
      disabled,
      isOpen,
      highlightedIndex,
      filteredOptions,
      closeMenu,
      openMenu,
      selectOption,
      search,
      typeAhead,
    ],
  );

  const wrapperClasses = [
    'fin-dropdown',
    error && 'fin-dropdown--error',
    disabled && 'fin-dropdown--disabled',
    isFilled && 'fin-dropdown--filled',
    isOpen && 'fin-dropdown--open',
    fluid && 'fin-dropdown--fluid',
    variant === 'inline' && 'fin-dropdown--inline',
    variant === 'field' && 'fin-dropdown--field',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const listboxId = `${id}-listbox`;

  return (
    <div className={wrapperClasses} ref={containerRef} style={style} aria-label={ariaLabel} data-testid={dataTestId}>
      {label && (
        <label className="fin-dropdown__label" id={`${id}-label`}>
          {label}
          {required && <span className="fin-dropdown__required">*</span>}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        className="fin-dropdown__trigger"
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-invalid={!!error}
        aria-describedby={
          error ? `${id}-error` : helperText ? `${id}-helper` : undefined
        }
        aria-activedescendant={
          isOpen && highlightedIndex >= 0
            ? `${id}-option-${highlightedIndex}`
            : undefined
        }
        id={id}
      >
        <span className="fin-dropdown__trigger-inner">
          {isOpen && search ? (
            <input
              ref={searchRef}
              className="fin-dropdown__search"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setHighlightedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder={selectedOption?.text || placeholder}
              aria-autocomplete="list"
              aria-controls={listboxId}
            />
          ) : (
            <span
              className={`fin-dropdown__trigger-text${!selectedOption ? ' fin-dropdown__trigger-text--placeholder' : ''}`}
            >
              {selectedOption?.text || placeholder}
            </span>
          )}

          {clearable && isFilled && !disabled && (
            <button
              type="button"
              className="fin-dropdown__clear"
              onClick={handleClear}
              aria-label="Clear selection"
              tabIndex={-1}
            >
              &times;
            </button>
          )}
        </span>

        <span className="fin-dropdown__chevron" aria-hidden="true">
          <DropdownChevronIcon color="#868C99" />
        </span>
      </button>

      {isOpen &&
        createPortal(
          <ul
            ref={menuRef}
            className={`fin-dropdown__menu${
              variant === 'inline'
                ? ' fin-dropdown__menu--inline'
                : variant === 'field'
                  ? ' fin-dropdown__menu--field'
                  : ''
            }`}
            role="listbox"
            id={listboxId}
            aria-labelledby={label ? `${id}-label` : undefined}
            style={menuStyle}
          >
            {filteredOptions.length === 0 ? (
              <li className="fin-dropdown__no-results">No results found</li>
            ) : (
              filteredOptions.map((opt, index) => (
                <li
                  key={opt.value}
                  id={`${id}-option-${index}`}
                  className={[
                    'fin-dropdown__option',
                    index === highlightedIndex &&
                      'fin-dropdown__option--active',
                    opt.value === value && 'fin-dropdown__option--selected',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  role="option"
                  aria-selected={opt.value === value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectOption(opt.value);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {opt.text}
                </li>
              ))
            )}
          </ul>,
          document.body,
        )}

      {error && (
        <p className="fin-dropdown__error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
      {!error && helperText && (
        <p className="fin-dropdown__helper" id={`${id}-helper`}>
          {helperText}
        </p>
      )}
    </div>
  );
}
